import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import './TimeSeriesChart.css';
import PixelIcon from './PixelIcon';
import { 
  THRESHOLDS,
  SYSTEM_LIVE_DATE,
  getCurrentChapter,
  computeCurrentStatus,
  mapTimestampToChapter
} from '../utils/timelineConfig';
import {
  buildObservedSeries,
  buildEventAnnotations,
  filterDataByTimeRange,
  formatDateForDisplay
} from '../utils/chartDataBuilder';

// Axis ticks carry the year: the timeline spans 2025-2028
const formatTick = (dateRaw) =>
  new Date(dateRaw).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

// Chart configuration constants
const CHART_HEIGHT = 450;

function TimeSeriesChart({ data }) {
  const [timeRange, setTimeRange] = useState('all');
  const [zoomLevel, setZoomLevel] = useState(1); // 1 = fitting/default, >1 = scrolling

  // Process data with memoization for performance
  const { observedData, projections, visibleEvents, currentStatus, currentChapter, nowDate } = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        observedData: [],
        projections: { racingScenario: [], slowdownScenario: [] },
        visibleEvents: [],
        currentStatus: null,
        currentChapter: null,
        nowDate: new Date().toISOString().split('T')[0]
      };
    }

    // Build observed series from backend data
    const observed = buildObservedSeries(data);
    
    // Filter based on time range
    const filteredObserved = filterDataByTimeRange(observed, timeRange);
    
    // Build scenario projections
    // Scenario projections were hand-drawn straight lines, not a model: not shown
    const projs = { racingScenario: [], slowdownScenario: [] };
    const filteredRacingScenario = filterDataByTimeRange(projs.racingScenario, timeRange);
    const filteredSlowdownScenario = filterDataByTimeRange(projs.slowdownScenario, timeRange);
    
    // Get visible events
    const firstDataDate = filteredObserved.length > 0 
      ? filteredObserved[0].dateObj 
      : (data && data.length > 0 ? new Date(data[0].date) : new Date());
      
    // If 'all', we want to see ALL events, even before data starts (if pertinent to narrative)
    // But data typically starts late 2025.
    // Let's set start date for events to TIMELINE START if 'all'
    const eventsStartDate = timeRange === 'all' 
      ? new Date('2025-05-01') // Start of C0 (Mid 2025)
      : firstDataDate;

    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + 18); // Show events up to 18 months in future
    const events = buildEventAnnotations(eventsStartDate, endDate);
    
    // Compute current status
    const latestObserved = observed[observed.length - 1];
    const status = latestObserved ? computeCurrentStatus(latestObserved.raceIndex) : null;
    
    // Get current chapter
    const chapter = getCurrentChapter();
    
    return {
      observedData: filteredObserved,
      projections: {
        racingScenario: filteredRacingScenario,
        slowdownScenario: filteredSlowdownScenario
      },
      visibleEvents: events,
      currentStatus: status,
      currentChapter: chapter,
      nowDate: new Date().toISOString().split('T')[0]
    };
  }, [data, timeRange]);

  // Combine all data for the chart
  const chartData = useMemo(() => {
    // Create a map to merge data points with same date
    const dataMap = new Map();
    
    // Add observed data
    observedData.forEach(point => {
      dataMap.set(point.date, {
        date: formatDateForDisplay(point.date),
        dateRaw: point.date,
        raceIndex: point.raceIndex,
        slowdownIndex: point.slowdownIndex,
        chapter: point.chapter?.label
      });
    });
    
    // Add projection data
    projections.racingScenario.forEach(point => {
      const existing = dataMap.get(point.date);
      if (existing) {
        existing.raceIndexRacing = point.raceIndex;
        existing.slowdownIndexRacing = point.slowdownIndex;
      } else {
        dataMap.set(point.date, {
          date: formatDateForDisplay(point.date),
          dateRaw: point.date,
          raceIndexRacing: point.raceIndex,
          slowdownIndexRacing: point.slowdownIndex,
          chapter: mapTimestampToChapter(point.date)?.label
        });
      }
    });
    
    projections.slowdownScenario.forEach(point => {
      const existing = dataMap.get(point.date);
      if (existing) {
        existing.raceIndexSlowdown = point.raceIndex;
        existing.slowdownIndexSlowdown = point.slowdownIndex;
      } else {
        dataMap.set(point.date, {
          date: formatDateForDisplay(point.date),
          dateRaw: point.date,
          raceIndexSlowdown: point.raceIndex,
          slowdownIndexSlowdown: point.slowdownIndex,
          chapter: mapTimestampToChapter(point.date)?.label
        });
      }
    });

    // CRITICAL: Ensure all visible events are present in the map
    // This allows us to draw vertical lines even if we have no data for that day
    visibleEvents.forEach(event => {
      const eventDateStr = event.date.toISOString().split('T')[0];
      if (!dataMap.has(eventDateStr)) {
         dataMap.set(eventDateStr, {
           date: formatDateForDisplay(eventDateStr),
           dateRaw: eventDateStr,
           chapter: event.chapter,
           isEventOnly: true
         });
      }
    });

    // If 'all' is selected, ensure we extend the timeline to the future (approx 15 months ahead)
    // to allow scrolling to see future events/chapters even if no projection data exists yet.
    if (timeRange === 'all' && dataMap.size > 0) {
      // Find the latest date currently in the map
      // Convert keys to timestamps for sorting
      const timestamps = Array.from(dataMap.keys()).map(d => new Date(d).getTime());
      const maxTimestamp = Math.max(...timestamps);
      const lastDate = new Date(maxTimestamp);
      
      const futureHorizon = new Date();
      futureHorizon.setMonth(futureHorizon.getMonth() + 15); // 15 months into the future

      // Fill in gaps if horizon is further than data
      if (lastDate < futureHorizon) {
        let curr = new Date(lastDate);
        curr.setDate(curr.getDate() + 1);
        
        while (curr <= futureHorizon) {
          const dateStr = curr.toISOString().split('T')[0];
          // Only add empty points if we don't have this date already
          if (!dataMap.has(dateStr)) {
            dataMap.set(dateStr, {
              date: formatDateForDisplay(dateStr),
              dateRaw: dateStr,
              chapter: mapTimestampToChapter(dateStr)?.label
              // No line values
            });
          }
          curr.setDate(curr.getDate() + 1);
        }
      }
    }
    
    // Convert map to sorted array
    // Numeric timestamps give the x axis real time proportions
    return Array.from(dataMap.values())
      .map(d => ({ ...d, ts: Date.parse(d.dateRaw) }))
      .sort((a, b) => a.ts - b.ts);
  }, [observedData, projections, timeRange, visibleEvents]);

  // Calculate generic chart width based on zoom
  const chartWidth = useMemo(() => {
    // We use a simple percentage based width to ensure predictable zooming behavior.
    // 1x = 100% (Fit/Original)
    // 2x = 200% (Scrolls)
    // ...
    return `${Math.max(100, zoomLevel * 100)}%`;
  }, [zoomLevel]);

  // Reset zoom when time range changes for better UX
  React.useEffect(() => {
    setZoomLevel(1);
  }, [timeRange]);

  // Setup scroll behavior
  const scrollContainerRef = React.useRef(null);
  
  // Scroll to "NOW" or end when data changes or zoom changes
  React.useEffect(() => {
    if (scrollContainerRef.current && zoomLevel > 0 && chartData.length > 0) {
      // Find index of "now" or last observed data
      // Just scroll to 20% to keep start visible or similar?
      // Actually, better to just let user scroll, or scroll to beginning?
      // scrollContainerRef.current.scrollLeft = 0;
    }
  }, [zoomLevel, timeRange, chartData.length]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const hasObserved = payload.some(p => p.name === 'Race Index' || p.name === 'Slowdown Index');
      const hasProjections = payload.some(p => p.name?.includes('Scenario'));
      
      return (
        <div className="custom-tooltip pixel-border-thin">
          <p className="tooltip-label text-cyan">{formatDateForDisplay(new Date(label).toISOString().split('T')[0], true)}</p>
          
          {hasObserved && <p style={{ fontSize: '12px', color: '#a0a8cc', marginTop: '5px' }}>OBSERVED DATA:</p>}
          {payload.filter(p => p.name === 'Race Index' || p.name === 'Slowdown Index').map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value?.toFixed(2) || 'N/A'}
            </p>
          ))}
          
          {hasProjections && <p style={{ fontSize: '12px', color: '#a0a8cc', marginTop: '8px', borderTop: '1px solid #252d4d', paddingTop: '5px' }}>PROJECTIONS:</p>}
          {payload.filter(p => p.name?.includes('Scenario')).map((entry, index) => (
            <p key={`proj-${index}`} style={{ color: entry.color, fontSize: '12px' }}>
              {entry.name}: {entry.value?.toFixed(2) || 'N/A'}
            </p>
          ))}
          
          {/* Show chapter if available */}
          {payload[0]?.payload?.chapter && (
            <p style={{ 
              fontSize: '12px', 
              color: '#4db8ff', 
              marginTop: '5px',
              borderTop: '1px solid #252b3d',
              paddingTop: '5px'
            }}>
              Chapter: {payload[0].payload.chapter}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="timeseries-chart-container">
      <div className="chart-title glow-text">
        <span className="title-icon"><PixelIcon name="chart" size={18} /></span>
        TEMPORAL EVOLUTION
        {currentChapter && (
          <span className="current-chapter-badge" style={{
            marginLeft: '20px',
            fontSize: '12px',
            padding: '5px 10px',
            background: 'rgba(77, 184, 255, 0.2)',
            border: '2px solid #4db8ff',
            borderRadius: '4px'
          }}>
            NOW: {currentChapter.label}
          </span>
        )}
      </div>
      
      {/* Current Status Badge */}
      {currentStatus && (
        <div className="status-badge" style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          marginBottom: '15px',
          padding: '12px',
          background: 'rgba(0, 0, 0, 0.3)',
          border: `2px solid ${currentStatus.color}`,
          borderRadius: '4px'
        }}>
          <div style={{ 
            fontFamily: "'JetBrains Mono', monospace", 
            fontSize: '12px',
            color: currentStatus.color,
            textShadow: `0 0 10px ${currentStatus.color}`
          }}>
            CURRENT STATUS: {currentStatus.status.toUpperCase()}
          </div>
          <div style={{ 
            fontFamily: "'JetBrains Mono', monospace", 
            fontSize: '12px',
            color: '#a0a8cc'
          }}>
            RACE INDEX: {currentStatus.raceIndex}
          </div>
          {currentStatus.status === 'Normal' && (
            <div style={{ 
              fontFamily: "'JetBrains Mono', monospace", 
              fontSize: '12px',
              color: '#a0a8cc'
            }}>
              DISTANCE TO CAUTION: {currentStatus.distanceToCaution}%
            </div>
          )}
        </div>
      )}
      
{/* Time range selector and Zoom */}
      <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          marginBottom: '20px',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace"
      }}>
        <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '5px' }}>
          {[
            { value: '1m', label: '1M' },
            { value: '3m', label: '3M' },
            { value: '6m', label: '6M' },
            { value: 'all', label: 'ALL' }
          ].map(range => (
            <button
              key={range.value}
              onClick={() => setTimeRange(range.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: timeRange === range.value ? '#4db8ff' : 'transparent',
                color: timeRange === range.value ? '#0f1219' : '#4db8ff',
                border: '2px solid #4db8ff',
                cursor: 'pointer',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '12px',
                transition: 'all 0.3s',
                whiteSpace: 'nowrap',
                boxShadow: timeRange === range.value ? '0 0 10px #4db8ff' : 'none'
              }}
            >
              {range.label}
            </button>
          ))}
        </div>

        {/* Zoom Controls */}
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#a8b2d1', marginRight: '5px' }}>ZOOM:</span>
          {/* Fit Screen Button */}
          <button
              onClick={() => setZoomLevel(1)}
              style={{
                padding: '8px',
                backgroundColor: zoomLevel === 1 ? '#4db8ff' : 'transparent',
                color: zoomLevel === 1 ? '#0f1219' : '#4db8ff',
                border: '2px solid #4db8ff',
                cursor: 'pointer',
                fontSize: '12px',
                fontFamily: 'monospace',
                minWidth: '30px'
              }}
              title="Fit to Screen"
          >
            FIT
          </button>
          
          <button
            onClick={() => setZoomLevel(prev => Math.max(1, prev - 1))}
            disabled={zoomLevel <= 1}
            style={{
              padding: '8px',
              backgroundColor: zoomLevel <= 1 ? 'rgba(77, 184, 255, 0.05)' : 'rgba(77, 184, 255, 0.1)',
              color: zoomLevel <= 1 ? '#2a4a66' : '#4db8ff',
              borderColor: zoomLevel <= 1 ? '#2a4a66' : '#4db8ff',
              borderStyle: 'solid',
              borderWidth: '2px',
              cursor: zoomLevel <= 1 ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontFamily: 'monospace',
              minWidth: '30px'
            }}
          >
            -
          </button>

          <span style={{ fontSize: '12px', color: '#4db8ff', minWidth: '15px', textAlign: 'center' }}>
            {zoomLevel}x
          </span>

          <button
            onClick={() => setZoomLevel(prev => Math.min(10, prev + 1))}
            style={{
              padding: '8px',
              backgroundColor: 'rgba(77, 184, 255, 0.1)',
              color: '#4db8ff',
              border: '2px solid #4db8ff',
              cursor: 'pointer',
              fontSize: '12px',
              fontFamily: 'monospace',
              minWidth: '30px'
            }}
          >
            +
          </button>
        </div>
      </div>
      
      <div className="chart-container-bordered">
        <div className="chart-scroll-view" ref={scrollContainerRef}>
          <div style={{ width: zoomLevel === 1 ? '100%' : chartWidth, minWidth: '100%', height: CHART_HEIGHT }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <defs>
                  <pattern id="low-data-hatch" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <rect width="10" height="10" fill="rgba(154, 166, 191, 0.05)" />
                    <rect width="4" height="10" fill="rgba(255, 209, 102, 0.13)" />
                  </pattern>
                </defs>
                {chartData.length > 0 && chartData[0].ts < SYSTEM_LIVE_DATE.getTime() && (
                  <ReferenceArea
                    x1={chartData[0].ts}
                    x2={SYSTEM_LIVE_DATE.getTime()}
                    fill="url(#low-data-hatch)"
                    fillOpacity={1}
                    stroke="none"
                    ifOverflow="hidden"
                    label={{
                      value: 'LOW DATA',
                      fill: '#ffd166',
                      fontSize: 11,
                      fontFamily: "'JetBrains Mono', monospace",
                      position: 'insideBottom'
                    }}
                  />
                )}
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="#252d4d"
                />
                <XAxis 
                  dataKey="ts"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={formatTick}
                  stroke="#4db8ff"
                  interval="preserveStartEnd"
                  minTickGap={30}
                  tick={{ 
                    fill: '#a8b2d1', 
                    fontSize: 11,
                    fontFamily: "'JetBrains Mono', monospace"
                  }}
                />
                <YAxis 
                  domain={[0, 3]}
                  stroke="#4db8ff"
                  ticks={[0, 0.75, 1.5, 2.25, 3]}
                  tick={{ 
                    fill: '#a8b2d1', 
                    fontSize: 11,
                    fontFamily: "'JetBrains Mono', monospace"
                  }}
                />
                
                {/* Threshold lines: they apply to the Race Index (red line) only */}
                <ReferenceLine 
                  y={THRESHOLDS.DANGER} 
                  stroke="#ff6b8f" 
                  strokeOpacity={0.6}
                  strokeDasharray="5 5"
                  label={{ 
                    value: `RACE DANGER ${THRESHOLDS.DANGER}`, 
                    fill: '#ff6b8f', 
                    fontSize: 11, 
                    fontFamily: "'JetBrains Mono', monospace",
                    position: 'insideBottomLeft'
                  }}
                />
                <ReferenceLine 
                  y={THRESHOLDS.CAUTION} 
                  stroke="#ffd166" 
                  strokeOpacity={0.6}
                  strokeDasharray="5 5"
                  label={{ 
                    value: `RACE CAUTION ${THRESHOLDS.CAUTION}`, 
                    fill: '#ffd166', 
                    fontSize: 11, 
                    fontFamily: "'JetBrains Mono', monospace",
                    position: 'insideBottomLeft'
                  }}
                />
                
                {/* NOW indicator */}
                {nowDate && chartData.some(d => d.dateRaw === nowDate) && (
                  <ReferenceLine 
                    x={Date.parse(nowDate)}
                    stroke="#4fe3a8" 
                    strokeWidth={2}
                    label={{ 
                      value: 'NOW', 
                      fill: '#4dffb8', 
                      fontSize: 11, 
                      fontFamily: "'JetBrains Mono', monospace",
                      position: 'top'
                    }}
                  />
                )}
                
                {/* Milestone markers (AI 2027) */}
                {visibleEvents.map(event => {
                  const x = event.date.getTime();
                  if (chartData.length === 0 || x < chartData[0].ts || x > chartData[chartData.length - 1].ts) return null;
                  const past = event.date <= new Date();
                  return (
                    <ReferenceLine
                      key={event.id}
                      x={x}
                      stroke={event.bifurcation ? '#ffd166' : '#5cc2ff'}
                      strokeWidth={event.bifurcation ? 2 : 1}
                      strokeDasharray={past ? undefined : '3 4'}
                      strokeOpacity={past ? 0.55 : 0.35}
                      label={{
                        value: event.id,
                        fill: event.bifurcation ? '#ffd166' : '#9aa6bf',
                        fontSize: 11,
                        fontFamily: "'JetBrains Mono', monospace",
                        position: 'insideTopLeft'
                      }}
                    />
                  );
                })}
                
                <Tooltip content={<CustomTooltip />} />
                
                {/* Observed data lines */}
                <Line 
                  type="monotone" 
                  dataKey="raceIndex" 
                  name="Race Index"
                  stroke="#ff6b8f" 
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="slowdownIndex" 
                  name="Slowdown Index"
                  stroke="#4fe3a8" 
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                  connectNulls={false}
                />
                
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Custom Legend OUTSIDE scroll view */}
        <div className="custom-chart-legend" style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          justifyContent: 'center',
          marginTop: '15px',
          padding: '10px',
          borderTop: '1px solid #252d4d'
        }}>
          {/* Observed */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '12px', height: '3px', background: '#ff6b8f' }}></div>
            <span style={{ fontSize: '12px', fontFamily: "'JetBrains Mono', monospace", color: '#a0a8cc' }}>Race Index</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '12px', height: '3px', background: '#4fe3a8' }}></div>
            <span style={{ fontSize: '12px', fontFamily: "'JetBrains Mono', monospace", color: '#a0a8cc' }}>Slowdown Index</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div className="low-data-swatch"></div>
            <span style={{ fontSize: '12px', fontFamily: "'JetBrains Mono', monospace", color: '#a0a8cc' }}>Low data: system not live yet</span>
          </div>
        </div>

      </div>
      

    </div>
  );
}

export default TimeSeriesChart;
